declare global {
  interface Window {
    Cashfree?: (config: { mode: 'sandbox' | 'production' }) => {
      checkout: (options: {
        paymentSessionId: string;
        redirectTarget?: string;
      }) => Promise<{ error?: { message?: string } }>;
      subscriptionsCheckout: (options: {
        subsSessionId: string;
        redirectTarget?: string;
      }) => Promise<{ error?: { message?: string } }>;
    };
    Razorpay?: any;
  }
}

const loaders: Record<string, Promise<void> | null> = {};

const loadScript = (id: string, src: string, global: 'Cashfree' | 'Razorpay') => {
  if (window[global]) return Promise.resolve();
  if (loaders[id]) return loaders[id]!;
  loaders[id] = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loaders[id] = null;
      reject(new Error(`Failed to load ${global} SDK`));
    };
    document.body.appendChild(script);
  });
  return loaders[id]!;
};

export const loadCashfreeSdk = () =>
  loadScript('cashfree-sdk', 'https://sdk.cashfree.com/js/v3/cashfree.js', 'Cashfree');

export const loadRazorpaySdk = () =>
  loadScript('razorpay-checkout-sdk', 'https://checkout.razorpay.com/v1/checkout.js', 'Razorpay');

export type CheckoutOutcome = 'paid' | 'cancelled';

/** Opens Razorpay's modal; resolves when the handler fires or the modal is dismissed. */
export const openRazorpayCheckout = async (options: {
  key: string;
  orderId: string;
  isSubscription?: boolean;
  amount?: number;
  currency?: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
}): Promise<CheckoutOutcome> => {
  await loadRazorpaySdk();
  if (!window.Razorpay) throw new Error('Razorpay SDK failed to load');
  return new Promise<CheckoutOutcome>((resolve, reject) => {
    try {
      const rzp = new window.Razorpay({
        key: options.key,
        ...(options.isSubscription
          ? { subscription_id: options.orderId }
          : { order_id: options.orderId }),
        amount: options.amount,
        currency: options.currency || 'INR',
        name: 'Payment',
        description: options.description,
        prefill: options.prefill || {},
        handler: () => resolve('paid'),
        modal: { ondismiss: () => resolve('cancelled') },
      });
      rzp.on?.('payment.failed', (res: any) =>
        reject(new Error(res?.error?.description || 'Payment failed.')),
      );
      rzp.open();
    } catch (err) {
      reject(err);
    }
  });
};

/** Opens Cashfree's in-page modal for a one-time payment. */
export const openCashfreeCheckout = async (
  paymentSessionId: string,
  environment: 'sandbox' | 'production',
): Promise<CheckoutOutcome> => {
  await loadCashfreeSdk();
  if (!window.Cashfree) throw new Error('Cashfree SDK failed to load');
  const result = await window
    .Cashfree({ mode: environment })
    .checkout({ paymentSessionId, redirectTarget: '_modal' });
  if (result?.error) {
    if (/aborted/i.test(result.error.message || '')) return 'cancelled';
    throw new Error('Payment failed.');
  }
  return 'paid';
};

/**
 * Cashfree subscriptions only support a full-page redirect to the hosted
 * authorization screen; the page navigates away and returns via return_url.
 */
export const startCashfreeSubscription = async (
  subsSessionId: string,
  environment: 'sandbox' | 'production',
): Promise<void> => {
  await loadCashfreeSdk();
  if (!window.Cashfree) throw new Error('Cashfree SDK failed to load');
  const result = await window
    .Cashfree({ mode: environment })
    .subscriptionsCheckout({ subsSessionId, redirectTarget: '_top' });
  if (result?.error) throw new Error(result.error.message || 'Payment failed.');
};

/** `payment_success`/`order_id` land on the form when it redirects back. */
export const REDIRECT_PROVIDERS = new Set(['hdfc']);
